import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  useTheme,
  LinearProgress,
  Avatar,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { toast } from "react-toastify";
import axios from "axios";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const CLOUDINARY_IMAGE_URL =
  "https://api.cloudinary.com/v1_1/dcmr4gnq5/image/upload";
const IMAGE_UPLOAD_PRESET = "book_uploads";

const AddEvent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLight = theme.palette.mode === "light";

  const [eventImg, setEventImg] = useState(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("Book Launch");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState("");
  const [price, setPrice] = useState("Free Entry");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const adminToken = localStorage.getItem("adminToken");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select JPEG, PNG, or WebP");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      e.target.value = "";
      return;
    }
    setEventImg(file);
  };

  const uploadImageToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", IMAGE_UPLOAD_PRESET);
    formData.append("cloud_name", "dcmr4gnq5");

    const response = await fetch(CLOUDINARY_IMAGE_URL, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) throw new Error(`Upload failed: ${response.status}`);

    const data = await response.json();
    if (data.error) throw new Error(data.error.message);
    if (!data.secure_url) throw new Error("No URL returned from Cloudinary");

    return data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) return;
    if (!eventImg) return toast.error("Please select an event image");
    if (
      !title.trim() ||
      !date ||
      !time.trim() ||
      !location.trim() ||
      !capacity.trim() ||
      !description.trim()
    )
      return toast.error("Please fill in all required fields");

    try {
      setUploading(true);
      toast.info("Uploading event image...");
      const coverImage = await uploadImageToCloudinary(eventImg);

      const { data } = await axios.post(
        BASE_URL + "/api/admin/add-event",
        {
          title: title.trim(),
          type,
          date,
          time: time.trim(),
          location: location.trim(),
          capacity: capacity.trim(),
          price: price.trim(),
          description: description.trim(),
          coverImage,
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (data.success) {
        toast.success(data.message || "Event added successfully!");
        setEventImg(null);
        setTitle("");
        setType("Book Launch");
        setDate("");
        setTime("");
        setLocation("");
        setCapacity("");
        setPrice("Free Entry");
        setDescription("");
        document.getElementById("event-img-input").value = "";
      } else {
        toast.error(data.message || "Failed to add event");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "An error occurred",
      );
    } finally {
      setUploading(false);
    }
  };

  const inputBg = isLight ? "#ffffff" : colors.primary[500];
  const inputText = isLight ? "#111827" : colors.grey[100];
  const inputLabel = isLight ? "#4b5563" : colors.grey[300];
  const inputBorder = isLight ? "#d1d5db" : colors.primary[300];
  const cardBg = isLight ? "#f9fafb" : colors.primary[400];
  const cardBorder = isLight ? "#e5e7eb" : colors.primary[300];

  const cardSx = {
    backgroundColor: cardBg,
    borderRadius: "16px",
    padding: "28px",
    border: `1px solid ${cardBorder}`,
  };

  const sectionLabelSx = {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: colors.greenAccent[isLight ? 500 : 400],
    mb: 3,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: inputBg,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: colors.greenAccent[400] },
      "&.Mui-focused fieldset": { borderColor: colors.greenAccent[400] },
    },
    "& .MuiInputLabel-root": { color: inputLabel },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.greenAccent[isLight ? 500 : 400],
    },
    "& .MuiInputBase-input": { color: inputText },
    "& .MuiSelect-icon": { color: inputLabel },
  };

  return (
    <Box m="20px">
      <Header title="ADD EVENT" subtitle="Add a new event to your catalog" />
      {uploading && (
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: 2,
            "& .MuiLinearProgress-bar": {
              backgroundColor: colors.greenAccent[400],
            },
            backgroundColor: isLight ? "#d1fae5" : colors.primary[300],
          }}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns="300px 1fr"
          gap="24px"
          alignItems="start"
        >
          {/* LEFT — Image Upload */}
          <Box sx={cardSx}>
            <Typography sx={sectionLabelSx}>
              <EventIcon sx={{ fontSize: 16 }} />
              Event Image
            </Typography>

            <label
              htmlFor="event-img-input"
              style={{ cursor: uploading ? "not-allowed" : "pointer" }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "16/10",
                  borderRadius: "12px",
                  border: `2px dashed ${eventImg ? colors.greenAccent[400] : isLight ? "#d1d5db" : colors.primary[300]}`,
                  backgroundColor: isLight ? "#ffffff" : colors.primary[500],
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: colors.greenAccent[400],
                    backgroundColor: isLight ? "#f0fdf4" : colors.primary[400],
                  },
                }}
              >
                {eventImg ? (
                  <img
                    src={URL.createObjectURL(eventImg)}
                    alt="Event preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box textAlign="center" p={3}>
                    <Avatar
                      sx={{
                        bgcolor: isLight ? "#d1fae5" : colors.greenAccent[700],
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <CloudUploadIcon
                        sx={{
                          color: isLight
                            ? colors.greenAccent[600]
                            : colors.greenAccent[300],
                        }}
                      />
                    </Avatar>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color={isLight ? "#374151" : colors.grey[200]}
                    >
                      Click to upload
                    </Typography>
                    <Typography
                      fontSize="11px"
                      color={isLight ? "#9ca3af" : colors.grey[400]}
                      mt={0.5}
                    >
                      JPEG, PNG, WebP · Max 10MB
                    </Typography>
                  </Box>
                )}
              </Box>
            </label>
            <input
              id="event-img-input"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageChange}
              disabled={uploading}
              hidden
            />

            {eventImg && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mt={2}
                p={1.5}
                sx={{
                  backgroundColor: isLight ? "#f0fdf4" : colors.primary[500],
                  borderRadius: "8px",
                  border: `1px solid ${isLight ? "#86efac" : colors.greenAccent[700]}`,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: isLight ? "#16a34a" : colors.greenAccent[400],
                    fontSize: 16,
                  }}
                />
                <Typography
                  fontSize="11px"
                  color={isLight ? "#166534" : colors.greenAccent[300]}
                  noWrap
                >
                  {eventImg.name}
                </Typography>
              </Box>
            )}
          </Box>

          {/* RIGHT — Fields */}
          <Box display="flex" flexDirection="column" gap="20px">
            {/* Event Details */}
            <Box sx={cardSx}>
              <Typography sx={sectionLabelSx}>
                <EventIcon sx={{ fontSize: 16 }} />
                Event Details
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="16px">
                <TextField
                  label="Event Title *"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  sx={{ ...inputSx, gridColumn: "span 2" }}
                  disabled={uploading}
                  required
                />
                <TextField
                  label="Event Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  select
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                >
                  {[
                    "Book Launch",
                    "Speaking Engagement",
                    "Team Building",
                    "Conference",
                    "Workshop",
                  ].map((t) => (
                    <MenuItem key={t} value={t} sx={{ color: inputText }}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  placeholder="Free Entry or KES 2,000"
                />
                <TextField
                  label="Date *"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  type="date"
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Time *"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  placeholder="e.g. 6:00 PM - 9:00 PM"
                  required
                />
              </Box>
            </Box>

            {/* Location & Capacity */}
            <Box sx={cardSx}>
              <Typography sx={sectionLabelSx}>
                <LocationOnIcon sx={{ fontSize: 16 }} />
                Location & Capacity
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="16px">
                <TextField
                  label="Location *"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  fullWidth
                  sx={{ ...inputSx, gridColumn: "span 2" }}
                  disabled={uploading}
                  required
                  placeholder="Event venue or online link"
                />
                <TextField
                  label="Capacity *"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  required
                  placeholder="e.g. 200 attendees"
                />
              </Box>
            </Box>

            {/* Description */}
            <Box sx={cardSx}>
              <Typography sx={sectionLabelSx}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                Description
              </Typography>
              <TextField
                label="About the Event *"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={5}
                fullWidth
                sx={inputSx}
                disabled={uploading}
                required
              />
              <Typography
                fontSize="11px"
                color={isLight ? "#9ca3af" : colors.grey[400]}
                textAlign="right"
                mt={1}
              >
                {description.length} characters
              </Typography>
            </Box>

            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? null : <AddIcon />}
              fullWidth
              sx={{
                backgroundColor: colors.greenAccent[isLight ? 500 : 600],
                color: isLight ? "#ffffff" : colors.primary[900],
                fontWeight: 700,
                fontSize: "14px",
                py: 1.8,
                borderRadius: "12px",
                textTransform: "none",
                letterSpacing: "0.05em",
                "&:hover": {
                  backgroundColor: colors.greenAccent[isLight ? 400 : 500],
                  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                },
                "&:disabled": {
                  backgroundColor: isLight ? "#d1d5db" : colors.primary[300],
                  color: isLight ? "#9ca3af" : colors.grey[500],
                },
              }}
            >
              {uploading ? "Adding Event..." : "Add Event"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default AddEvent;
